import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, CheckCircle, Clock, User, FileText, ArrowLeft } from "lucide-react";
import { db } from "@/firebase/config";
import { collection, query, where, getDocs, orderBy, onSnapshot } from "firebase/firestore";
import { Incident } from "./types";
import { format } from "date-fns";

interface StudentBehaviorProfileProps {
  schoolId: string;
  studentId: string;
  onBack: () => void;
}

interface StudentData {
  id: string;
  firstName: string;
  lastName: string;
  registrationNumber: string;
  className: string;
  photoUrl?: string;
  behaviorScore?: number;
  behaviorTier?: 1 | 2 | 3;
}

export const StudentBehaviorProfile = ({ schoolId, studentId, onBack }: StudentBehaviorProfileProps) => {
  const [student, setStudent] = useState<StudentData | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId || !studentId) return;

    const fetchStudent = async () => {
        // 1. Get Student Info
        // Assuming there is a student document at `schools/{schoolId}/students/{studentId}`
        // In a real app we might pass the student object prop to avoid refetching, but ID is safer for deep links
        try {
            const studentRef = collection(db, `schools/${schoolId}/students`);
            const q = query(studentRef, where("__name__", "==", studentId)); // Query by ID
            // Using getDocs query for consistent API even though it's a single doc fetch by ID
            // Alternatively `getDoc(doc(db, ...))`
            
            // For now, let's use the parent component's data passing if possible, 
            // but since we want this self-contained:
            const studentSnapshot = await getDocs(q);
             if (!studentSnapshot.empty) {
                const d = studentSnapshot.docs[0].data();
                setStudent({
                    id: studentSnapshot.docs[0].id,
                    firstName: d.firstName,
                    lastName: d.lastName,
                    registrationNumber: d.registrationNumber,
                    className: d.className || d.class || 'N/A',
                    photoUrl: d.photoUrl,
                    behaviorScore: d.behaviorScore ?? 100,
                    behaviorTier: d.behaviorTier ?? 1
                });
            }
        } catch (e) {
            console.error("Failed to load student", e);
        }
    };

    fetchStudent();

    // 2. Real-time Incidents
    const incidentsRef = collection(db, `schools/${schoolId}/disciplineIncidents`);
    const qIncidents = query(incidentsRef, where("studentId", "==", studentId), orderBy("timestamp", "desc"));
    
    const unsubscribe = onSnapshot(qIncidents, (snapshot) => {
        setIncidents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Incident)));
        setLoading(false);
    });

    return () => unsubscribe();
  }, [schoolId, studentId]);

  if (loading) return <div className="p-8 text-center">Loading profile...</div>;
  if (!student) return <div className="p-8 text-center">Student not found.</div>;

  const scoreColor = (score: number) => {
      if (score >= 90) return 'text-green-600';
      if (score >= 70) return 'text-orange-500';
      return 'text-red-600';
  };

  const getTierBadge = (tier: number) => {
      switch(tier) {
          case 3: return <Badge className="bg-red-600">Tier 3 (Intensive)</Badge>;
          case 2: return <Badge className="bg-orange-500">Tier 2 (Targeted)</Badge>;
          default: return <Badge className="bg-green-600">Tier 1 (Universal)</Badge>;
      }
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
      <Button variant="ghost" onClick={onBack} className="pl-0 hover:bg-transparent">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Directory
      </Button>

      {/* Header Profile Card */}
      <Card>
          <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="h-24 w-24 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border-4 border-white shadow-sm flex-shrink-0">
                      {student.photoUrl ? <img src={student.photoUrl} className="h-full w-full object-cover" /> : <User className="h-10 w-10 text-slate-400" />}
                  </div>
                  
                  <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                          <div>
                            <h2 className="text-2xl font-bold">{student.firstName} {student.lastName}</h2>
                            <p className="text-muted-foreground">{student.registrationNumber} • {student.className}</p>
                          </div>
                          <div className="text-right">
                              <p className="text-xs uppercase font-bold text-muted-foreground">Behavior Score</p>
                              <p className={`text-3xl font-bold ${scoreColor(student.behaviorScore || 100)}`}>{student.behaviorScore || 100}%</p>
                          </div>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-4">
                          {getTierBadge(student.behaviorTier || 1)}
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <FileText className="h-3 w-3" /> {incidents.length} Records
                          </span>
                      </div>
                  </div>
              </div>
          </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Timeline */}
          <div className="md:col-span-2 space-y-6">
              <h3 className="text-lg font-semibold">Incident History</h3>
              {incidents.length === 0 ? (
                  <Card>
                      <CardContent className="p-8 text-center text-muted-foreground">
                          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500/20" />
                          <p>No disciplinary records found.</p>
                          <p className="text-sm">This student has a clean record.</p>
                      </CardContent>
                  </Card>
              ) : (
                  <div className="relative border-l-2 border-slate-200 dark:border-slate-800 ml-3 space-y-8 pl-6 pb-4">
                      {incidents.map((incident) => (
                          <div key={incident.id} className="relative">
                              <div className={`absolute -left-[31px] top-1 h-4 w-4 rounded-full border-2 border-white dark:border-slate-950 ${
                                  incident.severity === 'critical' ? 'bg-red-500' : 
                                  incident.severity === 'major' ? 'bg-orange-500' : 'bg-blue-500'
                              }`} />
                              <Card className="mb-4">
                                  <CardHeader className="pb-2">
                                      <div className="flex justify-between items-start">
                                          <div>
                                              <CardTitle className="text-base">{incident.category}</CardTitle>
                                              <CardDescription>{format(new Date(incident.timestamp), 'PPP p')}</CardDescription>
                                          </div>
                                          <Badge variant="outline">{incident.status}</Badge>
                                      </div>
                                  </CardHeader>
                                  <CardContent>
                                      <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">{incident.description}</p>
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-slate-50 dark:bg-slate-900 p-2 rounded">
                                          <User className="h-3 w-3" /> Reported by {incident.reporterName}
                                          {incident.location && <><span className="mx-1">•</span> <span>{incident.location}</span></>}
                                      </div>
                                  </CardContent>
                              </Card>
                          </div>
                      ))}
                  </div>
              )}
          </div>

          {/* Sidebar: Interventions & Stats */}
          <div className="space-y-6">
              <Card>
                  <CardHeader>
                      <CardTitle className="text-base">Active Interventions</CardTitle>
                  </CardHeader>
                  <CardContent>
                      <div className="text-sm text-muted-foreground text-center py-4">
                          No active interventions.
                      </div>
                      <Button variant="outline" size="sm" className="w-full mt-2">
                          + Add Intervention
                      </Button>
                  </CardContent>
              </Card>

              <Card>
                  <CardHeader>
                      <CardTitle className="text-base">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                      <Button className="w-full justify-start" variant="outline">
                          <AlertCircle className="h-4 w-4 mr-2 text-orange-500" /> Log Minor Infraction
                      </Button>
                      <Button className="w-full justify-start" variant="outline">
                           <FileText className="h-4 w-4 mr-2 text-blue-500" /> View Attendance
                      </Button>
                  </CardContent>
              </Card>
          </div>
      </div>
    </div>
  );
};
